import React, { useState, useEffect, useMemo, useRef } from 'react';
import { patientRequestManager } from '../lib/patientRequestManager';
import {
  GENDER_OPTIONS,
  RELATION_OPTIONS,
  TREATMENT_TYPES,
  UNIT_SYSTEMS,
  DEFICITS_OPTIONS,
  getTreatmentTypeById,
  getVariantsForTreatmentType,
  getMedicalQuestionsForTreatmentType,
  getAdditionalInfoForTreatmentType
} from '../utils/appSettings';
import { parseLocalDate } from '../utils/dateUtils';
import './PatientRequestForm.css';

// Required fields validated on submit. Order matches visual field order so the
// error message points at the first empty field the user sees.
const REQUIRED_FIELDS = [
  { key: 'clinicInfo',    label: 'Clinic',         isEmpty: v => !v || !v.trim() },
  { key: 'idNumber',      label: 'Patient ID',     isEmpty: v => !v || !v.trim() },
  { key: 'dateOfBirth',   label: 'Date of Birth' },
  { key: 'gender',        label: 'Sex' },
  { key: 'height',        label: 'Height' },
  { key: 'weight',        label: 'Weight' },
  { key: 'treatmentType', label: 'Type of injury' },
  { key: 'dateOfEvent',   label: 'Date of Onset' },
];

/**
 * PatientRequestForm Component
 * Modal form for submitting new or editing existing patient enrollment requests
 *
 * Field structure matches the patient editor from react-admin:
 * 1. Patient ID (required)
 * 2. Date of Birth (full date)
 * 3. Sex
 * 4. Height / Weight
 * 5. Treatment Type (Injury Type)
 * 6. Treatment Type Variant (e.g., Affected Side for Stroke)
 * 7. Date of Onset
 * 8. Medical Questions (dynamic, based on treatment type)
 * 9. Caregiver Relationship
 */
const PatientRequestForm = ({
  isOpen,
  onClose,
  clinicId,
  clinicName,
  clinicType = 'MARKET',  // Clinic type to determine which additional fields to show (default to MARKET for this project)
  clinicPlaceholder = '',  // Configurable placeholder for clinic info field
  onSuccess,
  existingRequest = null,  // For editing existing pending requests
  readOnly = false  // View-only mode for non-editable (e.g. approved) requests
}) => {
  const isEditMode = !!existingRequest;

  const getInitialFormData = () => {
    if (existingRequest?.patientData) {
      const pd = existingRequest.patientData;
      return {
        clinicInfo: pd.clinicInfo || '',
        idNumber: pd.idNumber || '',
        dateOfBirth: pd.dateOfBirth || '',
        gender: pd.gender || '',
        height: pd.height || '',
        weight: pd.weight || '',
        unitSystem: pd.unitSystem || 'METRIC',
        treatmentType: pd.treatmentType || '',
        treatmentTypeVariant: pd.treatmentTypeVariant || '',
        dateOfEvent: pd.dateOfEvent || '',
        caregiverRelation: pd.caregiverRelation || '',
        medicalInfo: pd.medicalInfo || {},
        additionalInfo: pd.additionalInfo || {},
        deficits: pd.deficits || []
      };
    }
    return {
      clinicInfo: '',
      idNumber: '',
      dateOfBirth: '',
      gender: '',
      height: '',
      weight: '',
      unitSystem: 'METRIC',
      treatmentType: '',
      treatmentTypeVariant: '',
      dateOfEvent: '',
      caregiverRelation: '',
      medicalInfo: {},
      additionalInfo: {},
      deficits: []
    };
  };

  const [formData, setFormData] = useState(getInitialFormData);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [unitChangeNotice, setUnitChangeNotice] = useState(null);

  // Track initial treatment type to avoid resetting variant/medicalInfo on load
  const initialTreatmentTypeRef = useRef(null);

  // Get current unit system settings
  const currentUnits = UNIT_SYSTEMS[formData.unitSystem] || UNIT_SYSTEMS.METRIC;

  // Get selected treatment type details
  const selectedTreatmentType = useMemo(() => {
    return formData.treatmentType ? getTreatmentTypeById(formData.treatmentType) : null;
  }, [formData.treatmentType]);

  // Get variants for selected treatment type
  const variants = useMemo(() => {
    return formData.treatmentType ? getVariantsForTreatmentType(formData.treatmentType) : [];
  }, [formData.treatmentType]);

  // Get medical questions for selected treatment type
  const medicalQuestions = useMemo(() => {
    return formData.treatmentType ? getMedicalQuestionsForTreatmentType(formData.treatmentType) : [];
  }, [formData.treatmentType]);

  // Get additional info questions for MARKET clinic types
  const additionalInfoQuestions = useMemo(() => {
    if (!formData.treatmentType || !clinicType) return null;
    return getAdditionalInfoForTreatmentType(formData.treatmentType, clinicType);
  }, [formData.treatmentType, clinicType]);

  // Reset form data when existingRequest changes (for edit mode)
  useEffect(() => {
    if (isOpen) {
      const initialData = getInitialFormData();
      setFormData(initialData);
      setUnitChangeNotice(null);
      // Track initial treatment type so we don't reset variant/medicalInfo on load
      initialTreatmentTypeRef.current = initialData.treatmentType || null;
    }
  }, [isOpen, existingRequest]);

  // Reset variant, medical info and additional info when treatment type changes (but not on initial load)
  useEffect(() => {
    if (formData.treatmentType) {
      // Skip reset if this is the initial load with existing data
      if (initialTreatmentTypeRef.current === formData.treatmentType) {
        // Clear the ref so subsequent changes to the same type also trigger reset
        initialTreatmentTypeRef.current = null;
        return;
      }
      setFormData(prev => ({
        ...prev,
        treatmentTypeVariant: '',
        medicalInfo: {},
        additionalInfo: {}
      }));
    }
  }, [formData.treatmentType]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (name === 'height' || name === 'weight') {
      setUnitChangeNotice(null);
    }
  };

  const handleUnitSystemChange = (nextUnit) => {
    if (formData.unitSystem === nextUnit) {
      return;
    }
    const hadValues = Boolean(formData.height || formData.weight);
    setUnitChangeNotice(
      hadValues
        ? `Units changed to ${nextUnit === 'METRIC' ? 'Metric (cm/kg)' : 'Imperial (ft/lbs)'}. Please re-enter height and weight.`
        : null
    );
    setFormData(prev => ({ ...prev, unitSystem: nextUnit, height: '', weight: '' }));
  };

  const handleMedicalInfoChange = (questionId, value) => {
    setFormData(prev => ({
      ...prev,
      medicalInfo: {
        ...prev.medicalInfo,
        [questionId]: value
      }
    }));
  };

  const handleAdditionalInfoChange = (questionId, value) => {
    setFormData(prev => ({
      ...prev,
      additionalInfo: {
        ...prev.additionalInfo,
        [questionId]: value
      }
    }));
  };

  // Handle multiSelect toggle for additionalInfo
  const handleAdditionalInfoMultiSelectToggle = (questionId, optionId) => {
    setFormData(prev => {
      const currentValues = prev.additionalInfo[questionId] || [];
      const isSelected = currentValues.includes(optionId);
      return {
        ...prev,
        additionalInfo: {
          ...prev.additionalInfo,
          [questionId]: isSelected
            ? currentValues.filter(v => v !== optionId)
            : [...currentValues, optionId]
        }
      };
    });
  };

  const handleDeficitToggle = (deficitId) => {
    setFormData(prev => {
      const currentDeficits = prev.deficits || [];
      const isSelected = currentDeficits.includes(deficitId);
      return {
        ...prev,
        deficits: isSelected
          ? currentDeficits.filter(d => d !== deficitId)
          : [...currentDeficits, deficitId]
      };
    });
  };

  // Validation helpers
  const validateForm = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Date of Birth validation
    const dob = parseLocalDate(formData.dateOfBirth);
    if (dob) {
      if (dob > today) {
        return 'Date of Birth cannot be in the future';
      }
      const age = (today - dob) / (365.25 * 24 * 60 * 60 * 1000);
      if (age > 120) {
        return 'Date of Birth seems invalid (over 120 years ago)';
      }
    }

    // Date of Onset validation
    const doe = parseLocalDate(formData.dateOfEvent);
    if (doe) {
      if (doe > today) {
        return 'Date of Onset cannot be in the future';
      }
      // If both dates provided, onset must be after birth
      if (dob && doe < dob) {
        return 'Date of Onset cannot be before Date of Birth';
      }
    }

    // Height validation (if provided)
    if (formData.height) {
      if (formData.unitSystem === 'METRIC') {
        const height = parseFloat(formData.height);
        if (isNaN(height) || height <= 0) {
          return 'Height must be a positive number';
        }
        if (height < 30 || height > 300) {
          return 'Height must be between 30 and 300 cm';
        }
      } else {
        // Imperial - parse feet'inches format (e.g., "5'4" or "5'4\"" or just "64")
        const heightStr = formData.height.trim();
        let totalInches;
        const feetInchesMatch = heightStr.match(/^(\d+)'?\s*(\d*)["']?$/);
        if (feetInchesMatch) {
          const feet = parseInt(feetInchesMatch[1], 10);
          const inches = feetInchesMatch[2] ? parseInt(feetInchesMatch[2], 10) : 0;
          totalInches = feet * 12 + inches;
        } else {
          totalInches = parseFloat(heightStr);
        }
        if (isNaN(totalInches) || totalInches <= 0) {
          return 'Height must be a positive number (e.g., 5\'4 or 64)';
        }
        if (totalInches < 12 || totalInches > 96) {
          return 'Height must be between 1 and 8 feet';
        }
      }
    }

    // Weight validation (if provided)
    if (formData.weight) {
      const weight = parseFloat(formData.weight);
      if (isNaN(weight) || weight <= 0) {
        return 'Weight must be a positive number';
      }
      if (formData.unitSystem === 'METRIC') {
        if (weight < 1 || weight > 500) {
          return 'Weight must be between 1 and 500 kg';
        }
      } else {
        if (weight < 2 || weight > 1100) {
          return 'Weight must be between 2 and 1100 lbs';
        }
      }
    }

    return null; // No validation errors
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (readOnly) return;
    setError(null);
    setUnitChangeNotice(null);

    for (const field of REQUIRED_FIELDS) {
      const value = formData[field.key];
      const empty = field.isEmpty ? field.isEmpty(value) : !value;
      if (empty) {
        setError(`${field.label} is required`);
        return;
      }
      // Variant is required when the selected treatment type has variants.
      // Insert the check right after treatmentType so errors surface in visual order.
      if (field.key === 'treatmentType' && variants.length > 0 && !formData.treatmentTypeVariant) {
        setError(`${selectedTreatmentType?.variantsLabel || 'Variant'} is required`);
        return;
      }
    }

    // Validate required additionalInfo fields for MARKET clinic types
    if (additionalInfoQuestions) {
      for (const [key, field] of Object.entries(additionalInfoQuestions)) {
        if (field.required) {
          const value = formData.additionalInfo[key];
          // For multiSelect, check if array has items
          if (field.type === 'multiSelect') {
            if (!value || !Array.isArray(value) || value.length === 0) {
              setError(`${field.label} is required`);
              return;
            }
          } else {
            // For singleSelect and other types
            if (!value) {
              setError(`${field.label} is required`);
              return;
            }
          }
        }
      }
    }

    // Run field validations
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setSubmitting(true);

      const patientData = {
        clinicInfo: formData.clinicInfo.trim() || null,
        idNumber: formData.idNumber.trim(),
        dateOfBirth: formData.dateOfBirth || null,
        gender: formData.gender || null,
        height: formData.height || null,
        weight: formData.weight || null,
        unitSystem: formData.unitSystem,
        treatmentType: formData.treatmentType,
        treatmentTypeVariant: formData.treatmentTypeVariant || null,
        dateOfEvent: formData.dateOfEvent || null,
        caregiverRelation: formData.caregiverRelation || null,
        medicalInfo: Object.keys(formData.medicalInfo).length > 0 ? formData.medicalInfo : null,
        additionalInfo: Object.keys(formData.additionalInfo).length > 0 ? formData.additionalInfo : null,
        deficits: formData.deficits.length > 0 ? formData.deficits : null
      };

      let result;
      if (isEditMode) {
        result = await patientRequestManager.updateRequest(existingRequest.requestId, patientData);
      } else {
        result = await patientRequestManager.createRequest(clinicId, patientData);
      }

      if (result.success) {
        resetForm();
        if (onSuccess) {
          onSuccess(result, isEditMode);
        }
        onClose();
      } else {
        setError(result.error || `Failed to ${isEditMode ? 'update' : 'submit'} request`);
      }
    } catch (err) {
      console.error(`Error ${isEditMode ? 'updating' : 'submitting'} patient request:`, err);
      setError(err.message || `Failed to ${isEditMode ? 'update' : 'submit'} request`);
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    initialTreatmentTypeRef.current = null;
    setFormData({
      clinicInfo: '',
      idNumber: '',
      dateOfBirth: '',
      gender: '',
      height: '',
      weight: '',
      unitSystem: 'METRIC',
      treatmentType: '',
      treatmentTypeVariant: '',
      dateOfEvent: '',
      caregiverRelation: '',
      medicalInfo: {},
      additionalInfo: {},
      deficits: []
    });
  };

  const handleClose = () => {
    if (!submitting) {
      setError(null);
      setUnitChangeNotice(null);
      resetForm();
      onClose();
    }
  };

  const renderMedicalQuestion = (question) => {
    const value = formData.medicalInfo[question.id] || '';

    switch (question.type) {
      case 'singleSelect':
        return (
          <select
            value={value}
            onChange={(e) => handleMedicalInfoChange(question.id, e.target.value)}
            disabled={submitting || readOnly}
          >
            <option value="">Select...</option>
            {question.choices?.map(choice => (
              <option key={choice.id} value={choice.id}>{choice.name}</option>
            ))}
          </select>
        );

      case 'number':
        return (
          <input
            type="number"
            value={value}
            onChange={(e) => handleMedicalInfoChange(question.id, e.target.value)}
            placeholder={question.min !== undefined && question.max !== undefined
              ? `${question.min}-${question.max}`
              : ''}
            min={question.min}
            max={question.max}
            disabled={submitting || readOnly}
          />
        );

      case 'text':
      default:
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => handleMedicalInfoChange(question.id, e.target.value)}
            disabled={submitting || readOnly}
          />
        );
    }
  };

  // Render additionalInfo question (for MARKET clinic types)
  const renderAdditionalInfoQuestion = (questionKey, question) => {
    const value = formData.additionalInfo[questionKey];

    switch (question.type) {
      case 'singleSelect':
        return (
          <select
            value={value || ''}
            onChange={(e) => handleAdditionalInfoChange(questionKey, e.target.value)}
            disabled={submitting || readOnly}
          >
            <option value="">Select...</option>
            {question.options?.map(option => (
              <option key={option.id} value={option.id}>{option.name}</option>
            ))}
          </select>
        );

      case 'multiSelect': {
        const selectedValues = value || [];
        const selectedOptions = question.options?.filter(opt => selectedValues.includes(opt.id)) || [];
        const availableOptions = question.options?.filter(opt => !selectedValues.includes(opt.id)) || [];

        return (
          <div className="prf-multiselect-dropdown">
            {/* Dropdown to add selections */}
            <select
              value=""
              onChange={(e) => {
                if (e.target.value) {
                  handleAdditionalInfoMultiSelectToggle(questionKey, e.target.value);
                }
              }}
              disabled={submitting || readOnly || availableOptions.length === 0}
              className="prf-multiselect-add"
            >
              <option value="">
                {availableOptions.length === 0 ? 'All options selected' : 'Add selection...'}
              </option>
              {availableOptions.map(option => (
                <option key={option.id} value={option.id}>{option.name}</option>
              ))}
            </select>
            {/* Selected chips below */}
            {selectedOptions.length > 0 && (
              <div className="prf-multiselect-chips">
                {selectedOptions.map(option => (
                  <span key={option.id} className="prf-chip">
                    {option.name}
                    <button
                      type="button"
                      className="prf-chip-remove"
                      onClick={() => handleAdditionalInfoMultiSelectToggle(questionKey, option.id)}
                      disabled={submitting || readOnly}
                      aria-label={`Remove ${option.name}`}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        );
      }

      default:
        return (
          <input
            type="text"
            value={value || ''}
            onChange={(e) => handleAdditionalInfoChange(questionKey, e.target.value)}
            disabled={submitting || readOnly}
          />
        );
    }
  };

  if (!isOpen) return null;

  return (
    <div className="prf-overlay">
      <div className="prf-modal" onClick={e => e.stopPropagation()}>
        <button className="prf-close" onClick={handleClose} disabled={submitting}>
          &times;
        </button>

        <div className="prf-header">
          <h2>{readOnly ? 'View Patient Request' : isEditMode ? 'Edit Patient Request' : 'New Patient Request'}</h2>
          <p className="prf-clinic-name">{clinicName || 'Unknown Clinic'}</p>
        </div>

        <form onSubmit={handleSubmit} className="prf-form">
          {/* Clinic Info - First field with configurable placeholder (Required) */}
          <div className="prf-field prf-required">
            <label>Clinic</label>
            <input
              type="text"
              name="clinicInfo"
              value={formData.clinicInfo}
              onChange={handleInputChange}
              placeholder={clinicPlaceholder || 'Enter clinic info'}
              disabled={submitting || readOnly}
              required
            />
          </div>

          {/* Patient ID - Required */}
          <div className="prf-field prf-required">
            <label>Patient ID</label>
            <input
              type="text"
              name="idNumber"
              value={formData.idNumber}
              onChange={handleInputChange}
              placeholder="Enter patient identifier"
              disabled={submitting || readOnly}
              required
            />
          </div>

          {/* Date of Birth - Full Date */}
          <div className="prf-field prf-required">
            <label>Date of Birth</label>
            <input
              type="date"
              name="dateOfBirth"
              value={formData.dateOfBirth}
              onChange={handleInputChange}
              disabled={submitting || readOnly}
              required
            />
          </div>

          {/* Sex */}
          <div className="prf-field prf-required">
            <label>Sex</label>
            <select
              name="gender"
              value={formData.gender}
              onChange={handleInputChange}
              disabled={submitting || readOnly}
              required
            >
              <option value="">Select...</option>
              {GENDER_OPTIONS.map(opt => (
                <option key={opt.id} value={opt.id}>{opt.name}</option>
              ))}
            </select>
          </div>

          {/* Unit System Toggle */}
          <div className="prf-field">
            <label>Units</label>
            <div className="prf-unit-toggle">
              <button
                type="button"
                className={formData.unitSystem === 'METRIC' ? 'active' : ''}
                onClick={() => handleUnitSystemChange('METRIC')}
                disabled={submitting || readOnly}
              >
                Metric (cm/kg)
              </button>
              <button
                type="button"
                className={formData.unitSystem === 'IMPERIAL' ? 'active' : ''}
                onClick={() => handleUnitSystemChange('IMPERIAL')}
                disabled={submitting || readOnly}
              >
                Imperial (ft/lbs)
              </button>
            </div>
          </div>

          {/* Height / Weight */}
          <div className="prf-row">
            <div className="prf-field prf-required">
              <label>{currentUnits.height.label}</label>
              <input
                type="text"
                name="height"
                value={formData.height}
                onChange={handleInputChange}
                placeholder={currentUnits.height.placeholder}
                disabled={submitting || readOnly}
                required
              />
            </div>

            <div className="prf-field prf-required">
              <label>{currentUnits.weight.label}</label>
              <input
                type="text"
                name="weight"
                value={formData.weight}
                onChange={handleInputChange}
                placeholder={currentUnits.weight.placeholder}
                disabled={submitting || readOnly}
                required
              />
            </div>
          </div>

          {unitChangeNotice && (
            <div className="prf-notice" role="status">{unitChangeNotice}</div>
          )}

          {/* Treatment Type (Type of Injury) - Required */}
          <div className="prf-field prf-required">
            <label>{TREATMENT_TYPES.label}</label>
            <select
              name="treatmentType"
              value={formData.treatmentType}
              onChange={handleInputChange}
              disabled={submitting || readOnly}
              required
            >
              <option value="">Select...</option>
              {TREATMENT_TYPES.choices.map(opt => (
                <option key={opt.id} value={opt.id}>{opt.name}</option>
              ))}
            </select>
          </div>

          {/* Treatment Type Variant (e.g., Affected Side for Stroke) */}
          {selectedTreatmentType && variants.length > 0 && (
            <div className="prf-field prf-required">
              <label>{selectedTreatmentType.variantsLabel}</label>
              <select
                name="treatmentTypeVariant"
                value={formData.treatmentTypeVariant}
                onChange={handleInputChange}
                disabled={submitting || readOnly}
                required
              >
                <option value="">Select...</option>
                {variants.map(opt => (
                  <option key={opt.id} value={opt.id}>{opt.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Date of Onset */}
          <div className="prf-field prf-required">
            <label>Date of Onset</label>
            <input
              type="date"
              name="dateOfEvent"
              value={formData.dateOfEvent}
              onChange={handleInputChange}
              disabled={submitting || readOnly}
              required
            />
          </div>

          {/* Medical Questions Section - Dynamic based on treatment type */}
          {selectedTreatmentType && medicalQuestions.length > 0 && (
            <div className="prf-section">
              <h3 className="prf-section-title">Medical Information</h3>
              {medicalQuestions.map(question => (
                <div key={question.id} className="prf-field">
                  <label>{question.name}</label>
                  {renderMedicalQuestion(question)}
                </div>
              ))}
            </div>
          )}

          {/* Additional Info Section - For MARKET clinic types */}
          {additionalInfoQuestions && Object.keys(additionalInfoQuestions).length > 0 && (
            <div className="prf-section">
              <h3 className="prf-section-title">Additional {selectedTreatmentType?.name || 'Treatment'} Information</h3>
              {Object.entries(additionalInfoQuestions).map(([key, question]) => (
                <div key={key} className={`prf-field ${question.required ? 'prf-required' : ''}`}>
                  <label>{question.label}</label>
                  {renderAdditionalInfoQuestion(key, question)}
                </div>
              ))}
            </div>
          )}

          {/* Medical Deficits - Multi-select */}
          <div className="prf-section">
            <h3 className="prf-section-title">Medical Deficits</h3>
            <div className="prf-deficits-grid">
              {DEFICITS_OPTIONS.map(deficit => (
                <label key={deficit.id} className="prf-deficit-item">
                  <input
                    type="checkbox"
                    checked={formData.deficits.includes(deficit.id)}
                    onChange={() => handleDeficitToggle(deficit.id)}
                    disabled={submitting || readOnly}
                  />
                  <span>{deficit.name}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Caregiver Relationship */}
          <div className="prf-section">
            <h3 className="prf-section-title">Caregiver</h3>
            <div className="prf-field">
              <label>Relationship to Patient</label>
              <select
                name="caregiverRelation"
                value={formData.caregiverRelation}
                onChange={handleInputChange}
                disabled={submitting || readOnly}
              >
                <option value="">Select...</option>
                {RELATION_OPTIONS.map(opt => (
                  <option key={opt.id} value={opt.id}>{opt.name}</option>
                ))}
              </select>
            </div>
          </div>

          {error && <div className="prf-error">{error}</div>}

          <div className="prf-actions">
            <button
              type="button"
              className="prf-btn-cancel"
              onClick={handleClose}
              disabled={submitting}
            >
              {readOnly ? 'Close' : 'Cancel'}
            </button>
            {!readOnly && (
              <button
                type="submit"
                className="prf-btn-submit"
                disabled={submitting}
              >
                {submitting
                  ? (isEditMode ? 'Updating...' : 'Submitting...')
                  : (isEditMode ? 'Update Request' : 'Submit Request')
                }
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default PatientRequestForm;
